import React, { useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { EnrichedRiskArea } from '../../types/prevention';
import { RISK_LEVEL_CONFIG, STATUS_CONFIG, getRiskBand, FACTOR_CONFIG, FACTOR_SEVERITY_COLORS, RiskFactorScores, FactorSeverity } from '../RiskAreasWidget';
import { ChevronRight, MapPin, AlertTriangle, Clock, CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import { SifBadge, DataConfidenceBadge, ScheduledDateBadge, hasDataGap } from './shared';
interface Props {
  areas: EnrichedRiskArea[];
  onSelect: (area: EnrichedRiskArea) => void;
}
export function VariantDSplit({
  areas,
  onSelect
}: Props) {
  const [previewArea, setPreviewArea] = useState<EnrichedRiskArea | null>(areas[0] || null);
  if (areas.length === 0) return <div className="flex flex-col items-center justify-center h-full text-center py-16" data-id="element-2911">
        <CheckCircle2 size={48} className="text-emerald-400 mb-4" data-id="element-2912" />
        <p className="text-lg font-medium text-gray-900" data-id="element-2913">No activities found</p>
      </div>;
  return <div className="flex h-full overflow-hidden" data-id="element-2914">
      {/* Left: Narrow List */}
      <div className="w-1/3 min-w-[300px] border-r border-gray-200 overflow-y-auto bg-gray-50/50" data-id="element-2915">
        <div className="divide-y divide-gray-200" data-id="element-2916">
          {areas.map(area => {
          const config = RISK_LEVEL_CONFIG[area.riskLevel];
          const dataGap = hasDataGap(area);
          const isSelected = previewArea?.id === area.id;
          let rowClass = `w-full text-left p-4 transition-colors flex items-start gap-3 ${isSelected ? 'bg-white shadow-[inset_4px_0_0_0_#4f46e5]' : 'hover:bg-gray-100'}`;
          if (area.sifPotential) {
            rowClass = `w-full text-left p-4 transition-colors flex items-start gap-3 border-l-4 border-l-red-700 ${isSelected ? 'bg-red-50/80 shadow-[inset_4px_0_0_0_#b91c1c]' : 'bg-red-50/40 hover:bg-red-50/70'}`;
          } else if (dataGap) {
            rowClass = `w-full text-left p-4 transition-colors flex items-start gap-3 border-l-4 border-dashed border-l-amber-400 ${isSelected ? 'bg-amber-50/60 shadow-[inset_4px_0_0_0_#f59e0b]' : 'bg-amber-50/30 hover:bg-amber-50/50'}`;
          }
          return <button key={area.id} onClick={() => setPreviewArea(area)} className={rowClass} data-id="element-2917">
                <div className={`flex-shrink-0 w-10 py-1 rounded ${config.bgColor} flex flex-col items-center`} data-id="element-2918">
                  <span className={`text-[10px] font-bold ${config.color}`} data-id="element-2919">
                    {area.riskScore}
                  </span>
                </div>
                <div className="flex-1 min-w-0" data-id="element-2920">
                  <div className="flex items-center gap-1.5 mb-1" data-id="element-2921">
                    {area.sifPotential && <div className="mr-1" data-id="element-2922">
                        <SifBadge data-id="element-2923" />
                      </div>}
                    <span className="text-sm font-semibold text-gray-900 truncate" data-id="element-2924">
                      {area.project}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2" data-id="element-2925">
                    <p className="text-xs text-gray-500 truncate" data-id="element-2926">
                      {area.floor} · {area.location}
                    </p>
                    <ScheduledDateBadge date={area.scheduledDate} data-id="element-2927" />
                  </div>
                </div>
              </button>;
        })}
        </div>
      </div>

      {/* Right: Preview Panel */}
      <div className="flex-1 bg-white flex flex-col h-full" data-id="element-2928">
        {previewArea ? <>
            <div className="flex-1 overflow-y-auto p-8" data-id="element-2929">
              <div className="max-w-2xl mx-auto space-y-8" data-id="element-2930">
                {/* Header */}
                <div data-id="element-2931">
                  <div className="flex items-center gap-3 mb-4" data-id="element-2932">
                    <div className={`px-3 py-1.5 rounded-lg flex items-center gap-2 ${RISK_LEVEL_CONFIG[previewArea.riskLevel].bgColor}`} data-id="element-2933">
                      <span className={`text-sm font-bold ${RISK_LEVEL_CONFIG[previewArea.riskLevel].color}`} data-id="element-2934">
                        Score: {previewArea.riskScore}
                      </span>
                      {/* Mini Sparkline */}
                      {previewArea.scoreHistory && previewArea.scoreHistory.length > 0 && <svg width="40" height="16" viewBox="0 0 40 16" className="opacity-60" data-id="element-2935">
                            <polyline fill="none" stroke="currentColor" strokeWidth="1.5" points={previewArea.scoreHistory.slice(-7).map((h, i, arr) => {
                      const x = i / (arr.length - 1) * 40;
                      const min = Math.min(...arr.map(a => a.score));
                      const max = Math.max(...arr.map(a => a.score));
                      const range = max - min || 1;
                      const y = 16 - (h.score - min) / range * 16;
                      return `${x},${y}`;
                    }).join(' ')} data-id="element-2936" />
                          </svg>}
                    </div>
                    <span className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${STATUS_CONFIG[previewArea.status].bgColor} ${STATUS_CONFIG[previewArea.status].color}`} data-id="element-2937">
                      {createElement(STATUS_CONFIG[previewArea.status].icon, {
                    size: 12
                  })}
                      {STATUS_CONFIG[previewArea.status].label}
                    </span>
                    <DataConfidenceBadge confidence={previewArea.dataConfidence} data-id="element-2938" />
                    {previewArea.sifPotential && <SifBadge data-id="element-2939" />}
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2940">
                    {previewArea.project}
                  </h2>
                  <p className="text-gray-500 flex items-center gap-2" data-id="element-2941">
                    <MapPin size={16} data-id="element-2942" /> {previewArea.floor} —{' '}
                    {previewArea.location}
                  </p>
                </div>

                {/* Contacts */}
                {previewArea.contacts && previewArea.contacts.length > 0 && <div className="flex flex-wrap gap-4 bg-gray-50 p-3 rounded-lg border border-gray-100" data-id="element-2943">
                    {previewArea.contacts.slice(0, 2).map((contact, i) => <div key={i} className="flex items-center gap-2 text-sm" data-id="element-2944">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs" data-id="element-2945">
                          {contact.name.charAt(0)}
                        </div>
                        <div data-id="element-2946">
                          <p className="font-medium text-gray-900" data-id="element-2947">
                            {contact.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-2948">
                            {contact.role}
                          </p>
                        </div>
                        <a href={`tel:${contact.phone}`} className="ml-2 p-1.5 bg-white rounded-full shadow-sm text-gray-400 hover:text-indigo-600 transition-colors" data-id="element-2949">
                          <Phone size={14} data-id="element-2950" />
                        </a>
                      </div>)}
                  </div>}

                {/* Top Factors */}
                <div data-id="element-2951">
                  <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-2952">
                    Risk Drivers
                  </h3>
                  <div className="space-y-2" data-id="element-2953">
                    {previewArea.riskFactors.slice(0, 3).map((factor, i) => <div key={i} className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg" data-id="element-2954">
                        <AlertTriangle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" data-id="element-2955" />
                        <span className="text-sm text-gray-700" data-id="element-2956">{factor}</span>
                      </div>)}
                  </div>
                </div>

                {/* Recent Events */}
                {previewArea.events && previewArea.events.length > 0 && <div data-id="element-2957">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-2958">
                      Recent Events
                    </h3>
                    <div className="space-y-3" data-id="element-2959">
                      {previewArea.events.slice(0, 2).map(event => <div key={event.id} className="flex items-start gap-3" data-id="element-2960">
                          <div className="mt-0.5 w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" data-id="element-2961" />
                          <div data-id="element-2962">
                            <p className="text-sm text-gray-800" data-id="element-2963">
                              {event.description}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5" data-id="element-2964">
                              {event.date.toLocaleDateString()}{' '}
                              {event.date.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>}

                {/* Primary Action */}
                {previewArea.correctiveActions.length > 0 && <div data-id="element-2965">
                    <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3" data-id="element-2966">
                      Recommended Action
                    </h3>
                    <div className="bg-indigo-50 border border-indigo-100 p-4 rounded-xl" data-id="element-2967">
                      <p className="font-medium text-indigo-900 mb-2" data-id="element-2968">
                        {previewArea.correctiveActions[0].action}
                      </p>
                      {previewArea.correctiveActions[0].rationale && <p className="text-sm text-indigo-700 mb-3" data-id="element-2969">
                          {previewArea.correctiveActions[0].rationale}
                        </p>}
                      {previewArea.correctiveActions[0].expectedImpactPercent && <div className="inline-flex items-center gap-1 bg-white text-indigo-600 text-xs font-bold px-2 py-1 rounded shadow-sm" data-id="element-2970">
                          Est. Impact: -
                          {previewArea.correctiveActions[0].expectedImpactPercent}
                          % Risk
                        </div>}
                    </div>
                  </div>}
              </div>
            </div>

            {/* Full Details Button (Sticky Footer) */}
            <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-gray-50" data-id="element-2971">
              <div className="max-w-2xl mx-auto" data-id="element-2972">
                <button onClick={() => onSelect(previewArea)} className="w-full flex items-center justify-center gap-2 bg-gray-900 text-white px-6 py-3.5 rounded-xl font-medium hover:bg-gray-800 transition-colors shadow-sm" data-id="element-2973">
                  View Full Details & Take Action <ArrowRight size={18} data-id="element-2974" />
                </button>
              </div>
            </div>
          </> : <div className="flex items-center justify-center h-full text-gray-400" data-id="element-2975">
            Select a risk area to preview
          </div>}
      </div>
    </div>;
}